import { Injectable } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { Action, NgxsOnInit, Selector, State, StateContext } from "@ngxs/store";
import { ClientNotification, ShowNotification } from "app/shared/notifications";
import { ProjectionNotificationsService } from "app/shared/notifications/projection-notification.service";
import { merge } from "rxjs";
import { catchError, mergeMap, tap } from "rxjs/operators";
import { LoadMemberCorrespondenceTypes } from "../member-correspondence-type/member-correspondence-type.actions";
import { LoadProfessionalDomiciles } from "../member-professional-domicile/member-professional-domicile.actions";
import { MunicipalityUtilsService } from "../municipalities/municipalities-utils.service";
import { MemberSecondaryWorkAddressCommandService } from "./member-secondary-work-address-command.service";
import { MemberSecondaryWorkAddressQueryService } from "./member-secondary-work-address-query.service";
import {
  AddSecondaryWorkAddress,
  AddSecondaryWorkAddressFailure,
  AddSecondaryWorkAddressSuccess,
  CancelProfessionalDomicileChange,
  CancelProfessionalDomicileChangeFailure,
  CancelProfessionalDomicileChangeSuccess,
  CancelSecondaryWorkAddress,
  CancelSecondaryWorkAddressFailure,
  CancelSecondaryWorkAddressSuccess,
  CancelWorkAddressEndDate,
  CancelWorkAddressEndDateFailure,
  CancelWorkAddressEndDateSuccess,
  CorrectSecondaryWorkAddress,
  CorrectSecondaryWorkAddressFailure,
  CorrectSecondaryWorkAddressSuccess,
  CorrectWorkAddressEndDate,
  CorrectWorkAddressEndDateFailure,
  CorrectWorkAddressEndDateSuccess,
  CorrectWorkAddressStartDate,
  DefineWorkAddressAsMainPracticePlace,
  DefineWorkAddressAsMainPracticePlaceFailure,
  DefineWorkAddressAsMainPracticePlaceSuccess,
  LoadSecondaryWorkAddress,
  LoadSecondaryWorkAddressFailure,
  LoadSecondaryWorkAddressSuccess,
  RemoveSecondaryWorkAddress,
  RemoveSecondaryWorkAddressFailure,
  RemoveSecondaryWorkAddressSuccess,
} from "./member-secondary-work-address.actions";
import { MemberSecondaryWorkAddress } from "./models/member-secondary-work-address.model";

export class MemberSecondaryWorkAddressStateModel {
  workAddresses: MemberSecondaryWorkAddress[] = [];
  workAddressesLoading = false;
  _memberId: string;
}

@State<MemberSecondaryWorkAddressStateModel>({
  name: "memberSecondaryWorkAddress",
  defaults: new MemberSecondaryWorkAddressStateModel(),
})
@Injectable()
export class MemberSecondaryWorkAddressState implements NgxsOnInit {
  constructor(
    private queryService: MemberSecondaryWorkAddressQueryService,
    private commandService: MemberSecondaryWorkAddressCommandService,
    private municipalitUtilsService: MunicipalityUtilsService,
    private translate: TranslateService,
    private projectionNotificationsService: ProjectionNotificationsService
  ) {}

  @Selector()
  static workAddresses(
    state: MemberSecondaryWorkAddressStateModel
  ): MemberSecondaryWorkAddress[] {
    return state.workAddresses;
  }

  @Selector()
  static loading(state: MemberSecondaryWorkAddressStateModel): boolean {
    return state.workAddressesLoading;
  }

  ngxsOnInit({
    dispatch,
    getState,
  }: StateContext<MemberSecondaryWorkAddressStateModel>) {
    merge(
      this.projectionNotificationsService.when(
        "SecondaryWorkAddressRegisteredEvent"
      ),
      this.projectionNotificationsService.when(
        "SecondaryWorkAddressRemovedEvent"
      ),
      this.projectionNotificationsService.when(
        "SecondaryWorkAddressCancelledEvent"
      ),
      this.projectionNotificationsService.when(
        "WorkAddressInformationCorrectedEvent"
      ),
      this.projectionNotificationsService.when(
        "WorkAddressEndDateCorrectedEvent"
      ),
      this.projectionNotificationsService.when(
        "WorkAddressEndDateCancelledEvent"
      ),
      this.projectionNotificationsService.when(
        "WorkAddressStartDateCorrectedEvent"
      ),
      this.projectionNotificationsService.when(
        "WorkAddressDefinedAsProfessionalDomicileEvent"
      ),
      this.projectionNotificationsService.when(
        "ResidenceDefinedAsProfessionalDomicileEvent"
      ),
      this.projectionNotificationsService.when(
        "ProfessionalDomicileCancelledEvent"
      )
    ).subscribe((_) => {
      dispatch(new LoadSecondaryWorkAddress(getState()._memberId));
      dispatch(new LoadProfessionalDomiciles(getState()._memberId));
      dispatch(new LoadMemberCorrespondenceTypes(getState()._memberId));
    });
  }

  @Action(LoadSecondaryWorkAddress)
  onLoad(
    {
      patchState,
      dispatch,
    }: StateContext<MemberSecondaryWorkAddressStateModel>,
    { memberId }: LoadSecondaryWorkAddress
  ) {
    patchState({ workAddressesLoading: true, _memberId: memberId });
    return this.queryService.getAll(memberId).pipe(
      tap((result) => dispatch(new LoadSecondaryWorkAddressSuccess(result))),
      catchError((err) => dispatch(new LoadSecondaryWorkAddressFailure(err)))
    );
  }

  @Action(LoadSecondaryWorkAddressSuccess)
  onLoadSuccess(
    { patchState }: StateContext<MemberSecondaryWorkAddressStateModel>,
    { data }: LoadSecondaryWorkAddressSuccess
  ) {
    patchState({ workAddresses: data, workAddressesLoading: false });
  }

  @Action(LoadSecondaryWorkAddressFailure)
  onLoadFailure({
    patchState,
  }: StateContext<MemberSecondaryWorkAddressStateModel>) {
    patchState({ workAddresses: [], workAddressesLoading: false });
  }

  @Action(AddSecondaryWorkAddress)
  onAddSecondaryWorkAddress(
    { dispatch }: StateContext<MemberSecondaryWorkAddressStateModel>,
    { memberId, data }: AddSecondaryWorkAddress
  ) {
    dispatch(
      new ShowNotification(
        ClientNotification.info(
          this.translate.instant(
            "memberSecondaryWorkAddress.addSecondaryWorkAddress"
          )
        )
      )
    );
    return this.municipalitUtilsService
      .provideMunipalityCodeForAddress(data.clinic.businessPlace)
      .pipe(
        mergeMap((code) => {
          data.municipalityCode = code;
          return this.commandService.addSecondaryWorkAdress(memberId, data);
        }),
        tap((res) => dispatch(new AddSecondaryWorkAddressSuccess(res, data))),
        catchError((err) => dispatch(new AddSecondaryWorkAddressFailure(err)))
      );
  }

  @Action(RemoveSecondaryWorkAddress)
  onRemoveSecondaryWorkAddress(
    { dispatch }: StateContext<MemberSecondaryWorkAddressStateModel>,
    {
      memberId,
      workAddressId,
      effectiveUntil,
      confirmDeleteDomicileProfessionel,
      domicileProfessionelToDeleteId,
    }: RemoveSecondaryWorkAddress
  ) {
    dispatch(
      new ShowNotification(
        ClientNotification.info(
          this.translate.instant(
            "memberSecondaryWorkAddress.removeSecondaryWorkAddress"
          )
        )
      )
    );
    return this.commandService
      .removeSecondaryWorkAddress(
        memberId,
        workAddressId,
        effectiveUntil,
        confirmDeleteDomicileProfessionel,
        domicileProfessionelToDeleteId
      )
      .pipe(
        tap((res) => dispatch(new RemoveSecondaryWorkAddressSuccess(res))),
        catchError((err) =>
          dispatch(new RemoveSecondaryWorkAddressFailure(err))
        )
      );
  }

  @Action(DefineWorkAddressAsMainPracticePlace)
  onDefineWorkAddressAsMainPracticePlace(
    { dispatch }: StateContext<MemberSecondaryWorkAddressStateModel>,
    {
      memberId,
      workAddressId,
      startingOn,
    }: DefineWorkAddressAsMainPracticePlace
  ) {
    dispatch(
      new ShowNotification(
        ClientNotification.info(
          this.translate.instant("memberSecondaryWorkAddress.defining")
        )
      )
    );
    return this.commandService
      .defineWorkAddressAsMainPracticePlace(memberId, workAddressId, startingOn)
      .pipe(
        tap((res) =>
          dispatch(new DefineWorkAddressAsMainPracticePlaceSuccess(res))
        ),
        catchError((err) =>
          dispatch(new DefineWorkAddressAsMainPracticePlaceFailure(err))
        )
      );
  }

  @Action(DefineWorkAddressAsMainPracticePlaceFailure)
  onDefineWorkAddressAsMainPracticePlaceFailure({
    patchState,
  }: StateContext<MemberSecondaryWorkAddressStateModel>) {}

  @Action(CorrectSecondaryWorkAddress)
  onCorrectSecondaryWorkAddress(
    { dispatch }: StateContext<MemberSecondaryWorkAddressStateModel>,
    { memberId, workAddressId, workAddress }: CorrectSecondaryWorkAddress
  ) {
    dispatch(
      new ShowNotification(
        ClientNotification.info(
          this.translate.instant(
            "memberSecondaryWorkAddress.correctSecondaryWorkAddress"
          )
        )
      )
    );
    return this.municipalitUtilsService
      .provideMunipalityCodeForAddress(workAddress.clinic.businessPlace)
      .pipe(
        mergeMap((code) => {
          workAddress.municipalityCode = code;
          return this.commandService.correctSecondaryWorkAdress(
            memberId,
            workAddressId,
            workAddress
          );
        }),
        tap((res) =>
          dispatch(
            new CorrectSecondaryWorkAddressSuccess(
              res,
              workAddressId,
              workAddress
            )
          )
        ),
        catchError((err) =>
          dispatch(new CorrectSecondaryWorkAddressFailure(err))
        )
      );
  }

  private converter(
    workAddress: any,
    workAddressId: string = null
  ): MemberSecondaryWorkAddress {
    return {
      effectiveOn: workAddress.effectiveOn,
      effectiveUntil: null,
      wheelchairAccess: workAddress.isWheelChairAccessible,
      isCurrent: true,
      companyName: null,
      address: workAddress.clinic.businessPlace,
      phone: workAddress.clinic.phone,
      phone2: null,
      fax: workAddress.clinic.fax,
      practiceType: {
        id: workAddress.practiceType,
        description: workAddress.practiceTypeDesc,
      },
      clinicId: workAddressId,
      clinicName: workAddress.clinic.name,
      clinicType: null,
      isCompany: workAddress.isCompanyAddress,
      isAssociate: workAddress.isAssociate,
      isSelfEmployed: workAddress.isSelfEmployed,
      isEmployee: workAddress.isEmployee,
      isPrincipal: workAddress.isPrincipal,
    };
  }

  @Action(CorrectWorkAddressEndDate)
  onCorrectWorkAddressEndDate(
    { dispatch }: StateContext<MemberSecondaryWorkAddressStateModel>,
    { memberId, workAddressId, effectiveUntil }: CorrectWorkAddressEndDate
  ) {
    dispatch(
      new ShowNotification(
        ClientNotification.info(
          this.translate.instant(
            "memberSecondaryWorkAddress.correctWorkAddressEndDate"
          )
        )
      )
    );
    return this.commandService
      .correctWorkAddressEndDate(memberId, workAddressId, effectiveUntil)
      .pipe(
        tap((res) => dispatch(new CorrectWorkAddressEndDateSuccess(res))),
        catchError((err) => dispatch(new CorrectWorkAddressEndDateFailure(err)))
      );
  }

  @Action(CancelWorkAddressEndDate)
  onCancelWorkAddressEndDate(
    { dispatch }: StateContext<MemberSecondaryWorkAddressStateModel>,
    { memberId, workAddressId, effectiveUntil }: CancelWorkAddressEndDate
  ) {
    dispatch(
      new ShowNotification(
        ClientNotification.info(
          this.translate.instant(
            "memberSecondaryWorkAddress.cancelWorkAddressEndDate"
          )
        )
      )
    );
    return this.commandService
      .cancelWorkAddressEndDate(memberId, workAddressId, effectiveUntil)
      .pipe(
        tap((res) => dispatch(new CancelWorkAddressEndDateSuccess(res))),
        catchError((err) => dispatch(new CancelWorkAddressEndDateFailure(err)))
      );
  }

  @Action(CorrectWorkAddressStartDate)
  onCorrectWorkAddressStartDate(
    { dispatch }: StateContext<MemberSecondaryWorkAddressStateModel>,
    { memberId, workAddressId, effectiveOn }: CorrectWorkAddressStartDate
  ) {
    dispatch(
      new ShowNotification(
        ClientNotification.info(
          this.translate.instant(
            "memberSecondaryWorkAddress.correctWorkAddressStartDate"
          )
        )
      )
    );
    return this.commandService
      .correctWorkAddressStartDate(memberId, workAddressId, effectiveOn)
      .pipe(
        tap((res) => dispatch(new CorrectWorkAddressEndDateSuccess(res))),
        catchError((err) => dispatch(new CorrectWorkAddressEndDateFailure(err)))
      );
  }

  @Action(CancelSecondaryWorkAddress)
  onCancelSecondaryWorkAddress(
    { dispatch }: StateContext<MemberSecondaryWorkAddressStateModel>,
    {
      memberId,
      workAddressId,
      effectiveUntil,
      confirmDeleteDomicileProfessionel,
      domicileProfessionelToDeleteId,
    }: CancelSecondaryWorkAddress
  ) {
    dispatch(
      new ShowNotification(
        ClientNotification.info(
          this.translate.instant(
            "memberSecondaryWorkAddress.removeSecondaryWorkAddress"
          )
        )
      )
    );
    return this.commandService
      .cancelSecondaryWorkAddress(
        memberId,
        workAddressId,
        effectiveUntil,
        confirmDeleteDomicileProfessionel,
        domicileProfessionelToDeleteId
      )
      .pipe(
        tap((res) => dispatch(new CancelSecondaryWorkAddressSuccess(res))),
        catchError((err) =>
          dispatch(new CancelSecondaryWorkAddressFailure(err))
        )
      );
  }

  @Action(CancelProfessionalDomicileChange)
  onCancelProfessionalDomicileChange(
    {
      patchState,
      dispatch,
    }: StateContext<MemberSecondaryWorkAddressStateModel>,
    {
      memberId,
      professionalDomicileId,
      effectiveDate,
    }: CancelProfessionalDomicileChange
  ) {
    dispatch(
      new ShowNotification(
        ClientNotification.info(
          this.translate.instant(
            "memberSecondaryWorkAddress.cancelFutureProfessionalDomicile"
          )
        )
      )
    );
    return this.commandService
      .cancelFutureProfessionalDomicile(
        memberId,
        professionalDomicileId,
        effectiveDate
      )
      .pipe(
        tap((_) =>
          dispatch(
            new CancelProfessionalDomicileChangeSuccess(professionalDomicileId)
          )
        ),
        catchError((err) =>
          dispatch(new CancelProfessionalDomicileChangeFailure(err))
        )
      );
  }
}
